import { useEffect, useRef } from "react";
import {
  BookOpenText,
  ChevronLeft,
  ChevronRight,
  FilePlus2,
  LibraryBig,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { ChapterList } from "@/components/story/chapter-list";
import { Button } from "@/components/ui/button";
import type { MainStoryView, SideStoryView } from "@/features/story";
import { cn } from "@/lib/utils";

type StoryWorkspaceRailProps = {
  activeChapterId: string | null;
  activeSideStoryId: string | null;
  allowCreate?: boolean;
  collapsed?: boolean;
  isCreatingSideStory: boolean;
  mainStory: MainStoryView | null;
  onCreateChapter: () => Promise<void>;
  onCreateSideStory: () => void;
  onOpenMainStory: () => void;
  onOpenChapter: (chapterId: string) => void;
  onOpenSideStory: (storyId: string) => void;
  onReorderChapters: (orderedChapterIds: string[]) => Promise<void>;
  onToggleCollapsed: () => void;
  sideStories: SideStoryView[];
};

export function StoryWorkspaceRail({
  activeChapterId,
  activeSideStoryId,
  allowCreate = true,
  collapsed = false,
  isCreatingSideStory,
  mainStory,
  onCreateChapter,
  onCreateSideStory,
  onOpenMainStory,
  onOpenChapter,
  onOpenSideStory,
  onReorderChapters,
  onToggleCollapsed,
  sideStories,
}: StoryWorkspaceRailProps) {
  const { t } = useTranslation();
  const mainStoryButtonRef = useRef<HTMLButtonElement | null>(null);
  const sideStoryButtonRefs = useRef<Record<string, HTMLButtonElement | null>>(
    {},
  );

  useEffect(() => {
    if (collapsed) {
      return;
    }

    if (activeSideStoryId) {
      sideStoryButtonRefs.current[activeSideStoryId]?.focus();
      return;
    }

    if (!isCreatingSideStory && mainStory?.mode === "continuous") {
      mainStoryButtonRef.current?.focus();
    }
  }, [activeSideStoryId, collapsed, isCreatingSideStory, mainStory?.mode]);

  if (collapsed) {
    return (
      <aside className="flex items-start justify-center border-r border-ink/10 pr-1 pt-2 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)]">
        <div className="flex items-center gap-1">
          <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("story.chapters.title")}
          </span>
          <button
            aria-label={t("story.rail.expand", {
              defaultValue: "Expand story rail",
            })}
            className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
            onClick={onToggleCollapsed}
            type="button"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="min-h-0 border-r border-ink/10 pr-4 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)] xl:max-h-[calc(100vh-8rem)] xl:overflow-hidden">
      <div className="flex h-full min-h-0 flex-col gap-6">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-clay">
              {t("story.rail.eyebrow")}
            </p>
            <h2 className="font-display text-3xl leading-none tracking-tight text-foreground">
              {t("story.rail.title")}
            </h2>
          </div>
          <button
            aria-label={t("story.rail.collapse", {
              defaultValue: "Collapse story rail",
            })}
            className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
            onClick={onToggleCollapsed}
            type="button"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="space-y-6 pb-4">
            {mainStory ? (
              <button
                aria-current={
                  !activeSideStoryId &&
                  !isCreatingSideStory &&
                  (mainStory.mode === "continuous" || activeChapterId)
                    ? "page"
                    : undefined
                }
                className={cn(
                  "flex w-full items-start gap-3 rounded-[1.35rem] px-4 py-3 text-left transition-colors",
                  !activeSideStoryId &&
                    !isCreatingSideStory &&
                    (mainStory.mode === "continuous" || activeChapterId)
                    ? "bg-[linear-gradient(150deg,rgba(22,17,13,0.92),rgba(66,45,29,0.82))] text-canvas"
                    : "bg-[#f2e7d6] text-muted-foreground hover:bg-[#ecdfcc] hover:text-foreground",
                )}
                onClick={onOpenMainStory}
                ref={mainStoryButtonRef}
                type="button"
              >
                <BookOpenText
                  className={cn(
                    "mt-0 h-5 w-5",
                    !activeSideStoryId &&
                      !isCreatingSideStory &&
                      (mainStory.mode === "continuous" || activeChapterId)
                      ? "text-canvas"
                      : "",
                  )}
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <p
                    className={cn(
                      "text-sm font-medium",
                      !activeSideStoryId &&
                        !isCreatingSideStory &&
                        (mainStory.mode === "continuous" || activeChapterId)
                        ? "text-canvas"
                        : "text-foreground",
                    )}
                  >
                    {t("story.rail.mainStory")}
                  </p>
                  <p
                    className={cn(
                      "text-[10px] uppercase tracking-wider",
                      !activeSideStoryId &&
                        !isCreatingSideStory &&
                        (mainStory.mode === "continuous" || activeChapterId)
                        ? "text-canvas/70"
                        : "text-muted-foreground",
                    )}
                  >
                    {t(`story.modes.${mainStory.mode}`)}
                  </p>
                </div>
              </button>
            ) : null}

            {mainStory?.mode === "chapters" ? (
              <div className="min-h-0 space-y-4 overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-1">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold tracking-tight text-foreground">
                      {t("story.chapters.title")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("story.chapters.reorderHint")}
                    </p>
                  </div>
                  {allowCreate ? (
                    <Button
                      className="h-8 shadow-none"
                      onClick={() => void onCreateChapter()}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <Plus className="mr-1.5 h-4 w-4" />
                      {t("story.chapters.addAction")}
                    </Button>
                  ) : null}
                </div>

                <div className="max-h-[min(48vh,42rem)] overflow-y-auto pr-1">
                  <ChapterList
                    activeChapterId={activeChapterId}
                    chapters={mainStory.chapters}
                    onOpenChapter={onOpenChapter}
                    onReorderChapters={onReorderChapters}
                  />
                </div>
              </div>
            ) : null}

            <div className="space-y-4 border-t border-ink/10 pt-5">
              <div className="px-1">
                <div className="rounded-[1.2rem] bg-[#f2e7d6] px-4 py-3">
                  <div className="flex items-start gap-3">
                    <LibraryBig className="mt-0.5 h-4 w-4 text-foreground/70" />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold tracking-tight text-foreground">
                        {t("story.sideStories.title")}
                      </p>
                      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                        {t("story.sideStories.standalone", {
                          defaultValue: "Standalone",
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 px-1">
                <div className="space-y-1">
                  <p className="text-sm text-foreground">
                    {t("story.sideStories.listTitle", {
                      defaultValue: "Stories",
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("story.sideStories.description")}
                  </p>
                </div>
                {allowCreate ? (
                  <Button
                    className="h-8 shadow-none"
                    onClick={onCreateSideStory}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    <FilePlus2 className="mr-1.5 h-4 w-4" />
                    {t("story.sideStories.newAction")}
                  </Button>
                ) : null}
              </div>

              <div className="space-y-2">
                {isCreatingSideStory ? (
                  <div
                    aria-current="page"
                    className="flex items-start gap-3 rounded-[1.35rem] border border-ink/15 bg-[linear-gradient(150deg,rgba(22,17,13,0.92),rgba(66,45,29,0.82))] px-4 py-3 text-canvas"
                  >
                    <LibraryBig className="mt-0 h-5 w-5 text-canvas" />
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-sm font-medium text-canvas">
                        {t("story.sideStories.createTitle")}
                      </p>
                      <p className="line-clamp-2 text-xs text-canvas/70">
                        {t("story.sideStories.createDescription")}
                      </p>
                    </div>
                  </div>
                ) : null}
                {sideStories.length ? (
                  sideStories.map((story) => (
                    <button
                      key={story.id}
                      aria-current={
                        story.id === activeSideStoryId ? "page" : undefined
                      }
                      className={cn(
                        "flex w-full items-start gap-3 rounded-[1.35rem] px-4 py-3 text-left transition-colors",
                        story.id === activeSideStoryId
                          ? "bg-[linear-gradient(150deg,rgba(22,17,13,0.92),rgba(66,45,29,0.82))] text-canvas"
                          : "bg-transparent text-muted-foreground hover:bg-[#f4eadc] hover:text-foreground",
                      )}
                      onClick={() => onOpenSideStory(story.id)}
                      ref={(element) => {
                        sideStoryButtonRefs.current[story.id] = element;
                      }}
                      type="button"
                    >
                      <LibraryBig
                        className={cn(
                          "mt-0 h-5 w-5",
                          story.id === activeSideStoryId ? "text-canvas" : "",
                        )}
                      />
                      <div className="min-w-0 flex-1 space-y-1">
                        <p
                          className={cn(
                            "text-sm font-medium",
                            story.id === activeSideStoryId
                              ? "text-canvas"
                              : "text-foreground",
                          )}
                        >
                          {story.title}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={cn(
                              "rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]",
                              story.id === activeSideStoryId
                                ? "border-canvas/25 bg-canvas/10 text-canvas/85"
                                : "border-clay/15 bg-[#f4eadc] text-clay",
                            )}
                          >
                            {t(story.canonStatusLabelKey)}
                          </span>
                          <span
                            className={cn(
                              "rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]",
                              story.id === activeSideStoryId
                                ? "border-canvas/25 bg-canvas/10 text-canvas/75"
                                : "border-clay/15 bg-white/70 text-muted-foreground",
                            )}
                          >
                            {t("story.modes.continuous")}
                          </span>
                        </div>
                        <p
                          className={cn(
                            "line-clamp-2 text-xs",
                            story.id === activeSideStoryId
                              ? "text-canvas/70"
                              : "text-muted-foreground",
                          )}
                        >
                          {story.summary}
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="rounded-[1.1rem] bg-[#f2e7d6]/55 px-3 py-4 text-center text-xs text-muted-foreground">
                    {t("story.sideStories.empty")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
